/* ark-quote-form.js (2026-07-10)
 * Runtime for the Ark Quote Form section: step navigation, quantity stepper,
 * live discount-tier meter, Cloudinary logo upload (existing infrastructure),
 * and AJAX submit to the native Shopify contact form so the page never
 * reloads or scroll-jumps. Progressive enhancement: without this file the
 * section renders all steps stacked and posts natively.
 */
(function () {
  'use strict';

  function initQuoteForm(root) {
    if (!root || root.dataset.qfReady === '1') return;
    root.dataset.qfReady = '1';
    root.classList.remove('ark-qf--nojs');

    var form = root.querySelector('form');
    if (!form) return;

    /* ---------- templates + config ---------- */
    function tpl(str, vars) {
      return String(str || '').replace(/\{(\w+)\}/g, function (m, k) {
        return (vars && vars[k] !== undefined) ? vars[k] : m;
      });
    }
    function esc(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
    /* Escapes the whole template, then swaps placeholders for escaped values wrapped
     * in <strong>. Only markup that can reach the DOM is the <strong> pair itself. */
    function tplHtml(str, vars) {
      return esc(String(str || '')).replace(/\{(\w+)\}/g, function (m, k) {
        return (vars && vars[k] !== undefined) ? '<strong>' + esc(vars[k]) + '</strong>' : m;
      });
    }
    var cfg = {
      tiers: [],
      chipLocked: root.dataset.chipLocked || 'Add {min}+ for a discount',
      chipUnlocked: root.dataset.chipUnlocked || '{pct}% off unlocked',
      discountNone: root.dataset.discountNone || 'No discount yet',
      discountActive: root.dataset.discountActive || '{pct}% off',
      nudgeNext: root.dataset.nudgeNext || 'Add {count} more to reach {min}+ and unlock {pct}% off.',
      nudgeTop: root.dataset.nudgeTop || 'You have unlocked our best team pricing.',
      stepCounter: root.dataset.stepCounter || 'Step {step} of {total}',
      sendingLabel: root.dataset.sendingLabel || 'Sending...',
      uploadCloud: root.dataset.uploadCloud || '',
      uploadPreset: root.dataset.uploadPreset || '',
      maxBytes: (parseInt(root.dataset.uploadMaxMb, 10) || 10) * 1024 * 1024,
      allowedExts: ['jpg', 'jpeg', 'png', 'pdf', 'ai', 'eps', 'svg', 'webp', 'tif', 'tiff', 'heic']
    };
    try {
      cfg.tiers = JSON.parse(root.dataset.tiers || '[]')
        .filter(function (t) { return t && isFinite(t.min) && isFinite(t.pct); })
        .sort(function (a, b) { return a.min - b.min; });
    } catch (e) { cfg.tiers = []; }
    var topMin = cfg.tiers.length ? cfg.tiers[cfg.tiers.length - 1].min : 250;

    /* ---------- elements ---------- */
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-qf-step]'));
    var totalSteps = panels.length || 3;
    var progressRow = root.querySelector('[data-qf-progress-row]');
    var progressBar = root.querySelector('[data-qf-progressbar]');
    var progressFill = root.querySelector('[data-qf-progress]');
    var counterEl = root.querySelector('[data-qf-counter]');
    var qtyInput = root.querySelector('[data-qf-qty]');
    var qtyDisplay = root.querySelector('[data-qf-qty-display]');
    var tierChip = root.querySelector('[data-qf-tier-chip]');
    var discountEl = root.querySelector('[data-qf-discount]');
    var meterEl = root.querySelector('[data-qf-meter]');
    var nudgeEl = root.querySelector('[data-qf-nudge]');
    var tierInput = root.querySelector('[data-qf-tier-input]');
    var rungs = Array.prototype.slice.call(root.querySelectorAll('[data-qf-rung]'));
    var ticks = Array.prototype.slice.call(root.querySelectorAll('[data-qf-tick]'));
    var presets = Array.prototype.slice.call(root.querySelectorAll('[data-qf-preset]'));
    var successPanel = root.querySelector('[data-qf-success]');
    var submitBtn = root.querySelector('[data-qf-submit]');

    /* ---------- tier engine ---------- */
    function currentQty() {
      var q = parseInt(qtyInput && qtyInput.value, 10);
      return isNaN(q) || q < 1 ? 1 : q;
    }
    function tierFor(q) {
      var t = null;
      cfg.tiers.forEach(function (x) { if (q >= x.min) t = x; });
      return t;
    }
    function nextTierFor(q) {
      for (var i = 0; i < cfg.tiers.length; i++) { if (q < cfg.tiers[i].min) return cfg.tiers[i]; }
      return null;
    }
    /* Checkpoint-anchored fill: tick i sits at (i + 0.5) / n of the track (the same
     * formula the Liquid uses to place the tick dots), and the fill interpolates
     * between those anchors. The bar therefore crosses each checkpoint at EXACTLY
     * that tier's quantity; fill and ticks can never disagree. Top tier fills 100%. */
    function tickPos(i) { return ((i + 0.5) / cfg.tiers.length) * 100; }
    function fillFor(q) {
      var n = cfg.tiers.length;
      if (!n) return 0;
      if (q >= cfg.tiers[n - 1].min) return 100;
      if (q < cfg.tiers[0].min) return (q / cfg.tiers[0].min) * tickPos(0);
      for (var i = 0; i < n - 1; i++) {
        if (q < cfg.tiers[i + 1].min) {
          var f = (q - cfg.tiers[i].min) / (cfg.tiers[i + 1].min - cfg.tiers[i].min);
          return tickPos(i) + f * (tickPos(i + 1) - tickPos(i));
        }
      }
      return 100;
    }
    function renderTier() {
      if (!qtyInput) return;
      var q = currentQty();
      var cur = tierFor(q);
      var nxt = nextTierFor(q);
      var pct = cur ? cur.pct : 0;

      if (qtyDisplay) qtyDisplay.textContent = q;
      if (tierChip) {
        if (pct > 0) {
          tierChip.textContent = tpl(cfg.chipUnlocked, { pct: pct });
          tierChip.classList.add('is-on');
        } else {
          tierChip.textContent = tpl(cfg.chipLocked, { min: cfg.tiers.length ? cfg.tiers[0].min : 10 });
          tierChip.classList.remove('is-on');
        }
      }
      if (discountEl) {
        discountEl.textContent = pct > 0 ? tpl(cfg.discountActive, { pct: pct }) : cfg.discountNone;
        discountEl.classList.toggle('is-zero', pct === 0);
      }
      if (meterEl) meterEl.style.width = fillFor(q) + '%';
      ticks.forEach(function (t) {
        t.classList.toggle('is-on', q >= parseInt(t.dataset.min, 10));
      });
      rungs.forEach(function (r) {
        var min = parseInt(r.dataset.min, 10);
        r.classList.toggle('is-on', q >= min);
        r.classList.toggle('is-current', cur !== null && cur.min === min);
      });
      if (nudgeEl) {
        nudgeEl.innerHTML = nxt
          ? tplHtml(cfg.nudgeNext, { count: nxt.min - q, min: nxt.min, pct: nxt.pct })
          : esc(cfg.nudgeTop);
      }
      if (tierInput) tierInput.value = pct + '%';
      presets.forEach(function (b) {
        b.classList.toggle('is-on', parseInt(b.dataset.qfPreset, 10) === q);
      });
      renderRail();
    }
    function setQty(q) {
      if (!qtyInput) return;
      qtyInput.value = Math.max(1, Math.min(100000, parseInt(q, 10) || 1));
      renderTier();
    }

    if (qtyInput) {
      qtyInput.addEventListener('input', renderTier);
      qtyInput.addEventListener('change', function () { setQty(qtyInput.value); });
    }
    presets.forEach(function (b) {
      b.addEventListener('click', function () { setQty(b.dataset.qfPreset); });
    });

    /* Tap = one step; press-and-hold = accelerating auto-repeat (mobile friendly). */
    function holdRepeat(btn, delta) {
      if (!btn) return;
      var t1, t2;
      function start(e) {
        if (e.cancelable) e.preventDefault();
        setQty(currentQty() + delta);
        var speed = 260;
        t1 = setTimeout(function tick() {
          setQty(currentQty() + delta);
          speed = Math.max(45, speed - 25);
          t2 = setTimeout(tick, speed);
        }, 380);
      }
      function stop() { clearTimeout(t1); clearTimeout(t2); }
      if (window.PointerEvent) {
        btn.addEventListener('pointerdown', start);
        ['pointerup', 'pointerleave', 'pointercancel'].forEach(function (ev) { btn.addEventListener(ev, stop); });
      } else {
        btn.addEventListener('mousedown', start);
        ['mouseup', 'mouseleave'].forEach(function (ev) { btn.addEventListener(ev, stop); });
        btn.addEventListener('touchstart', start, { passive: false });
        ['touchend', 'touchcancel'].forEach(function (ev) { btn.addEventListener(ev, stop); });
      }
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setQty(currentQty() + delta); }
      });
    }
    holdRepeat(root.querySelector('[data-qf-dec]'), -1);
    holdRepeat(root.querySelector('[data-qf-inc]'), 1);

    /* ---------- live summary rail + sticky mobile bar + named tracker ---------- */
    var railEls = {
      product: root.querySelector('[data-qf-rail-product]'),
      qty: root.querySelector('[data-qf-rail-qty]'),
      budget: root.querySelector('[data-qf-rail-budget]'),
      deco: root.querySelector('[data-qf-rail-deco]'),
      when: root.querySelector('[data-qf-rail-when]'),
      discount: root.querySelector('[data-qf-rail-discount]')
    };
    var barLine = root.querySelector('[data-qf-bar-line]');
    var barStep = root.querySelector('[data-qf-bar-step]');
    var pendingLabel = root.dataset.railPending || 'To be added';
    function checkedVal(name) {
      var el = form.querySelector('input[name="contact[' + name + ']"]:checked');
      return el ? el.value : '';
    }
    function railRow(el, val) {
      if (!el) return;
      el.textContent = val || pendingLabel;
      el.classList.toggle('is-pending', !val);
    }
    function renderRail() {
      var q = currentQty();
      var cur = tierFor(q);
      var pct = cur ? cur.pct : 0;
      var product = checkedVal('Product type');
      railRow(railEls.product, product);
      railRow(railEls.qty, q + ' units');
      railRow(railEls.budget, checkedVal('Budget range'));
      railRow(railEls.deco, checkedVal('Decoration'));
      var whenVal = checkedVal('Timing');
      var dEl = root.querySelector('[data-qf-date]');
      if (dEl && dEl.value && !root.querySelector('[data-qf-date-wrap]').hidden) whenVal = dEl.value;
      railRow(railEls.when, whenVal);
      if (railEls.discount) railEls.discount.textContent = pct > 0 ? tpl(cfg.discountActive, { pct: pct }) : cfg.discountNone;
      if (barLine) {
        barLine.textContent = q + ' ' + (product || '') + (pct > 0 ? ' · ' : '');
        if (pct > 0) {
          var em = document.createElement('em');
          em.textContent = pct + '% off';
          barLine.appendChild(em);
        }
      }
    }
    ['Product type', 'Budget range', 'Decoration', 'Timing'].forEach(function (nm) {
      form.querySelectorAll('input[name="contact[' + nm + ']"]').forEach(function (r) {
        r.addEventListener('change', renderRail);
      });
    });

    /* v7: "By a set date" reveals a date input; the picked date feeds the Ready-by rail row */
    var dateWrap = root.querySelector('[data-qf-date-wrap]');
    var dateInput = root.querySelector('[data-qf-date]');
    function syncDateReveal() {
      var checked = form.querySelector('input[name="contact[Timing]"]:checked');
      var show = !!(checked && checked.dataset.revealsDate);
      if (dateWrap) dateWrap.hidden = !show;
      if (!show && dateInput) dateInput.value = '';
    }
    form.querySelectorAll('input[name="contact[Timing]"]').forEach(function (r) {
      r.addEventListener('change', syncDateReveal);
    });
    if (dateInput) dateInput.addEventListener('change', renderRail);
    syncDateReveal();

    var tsteps = Array.prototype.slice.call(root.querySelectorAll('[data-qf-tstep]'));
    var tconns = Array.prototype.slice.call(root.querySelectorAll('[data-qf-tconn]'));
    function renderTracker() {
      tsteps.forEach(function (el) {
        var n = parseInt(el.dataset.qfTstep, 10);
        el.classList.toggle('is-done', step > n);
        el.classList.toggle('is-active', step === n);
      });
      tconns.forEach(function (el) {
        el.classList.toggle('is-done', step > parseInt(el.dataset.qfTconn, 10));
      });
      if (barStep) barStep.textContent = tpl(cfg.stepCounter, { step: step, total: totalSteps });
    }

    /* ---------- step navigation ---------- */
    var step = 1;
    function showStep(n, moveFocus) {
      step = Math.max(1, Math.min(totalSteps, n));
      var panel = null;
      panels.forEach(function (p) {
        var active = parseInt(p.dataset.qfStep, 10) === step;
        p.hidden = !active;
        if (active) panel = p;
      });
      if (progressFill) progressFill.style.width = ((step / totalSteps) * 100).toFixed(2) + '%';
      if (progressBar) progressBar.setAttribute('aria-valuenow', String(step));
      if (counterEl) counterEl.textContent = tpl(cfg.stepCounter, { step: step, total: totalSteps });
      renderTracker();
      if (moveFocus && panel) {
        panel.setAttribute('tabindex', '-1');
        try { panel.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
      }
    }
    root.querySelectorAll('[data-qf-next]').forEach(function (b) {
      b.addEventListener('click', function () { showStep(step + 1, true); });
    });
    root.querySelectorAll('[data-qf-back]').forEach(function (b) {
      b.addEventListener('click', function () { showStep(step - 1, true); });
    });

    /* ---------- logo upload: EXISTING Cloudinary unsigned-upload server ---------- */
    var fileInput = root.querySelector('[data-qf-file]');
    var dropzone = root.querySelector('[data-qf-dropzone]');
    var statusEl = root.querySelector('[data-qf-upload-status]');
    var logoUrlInput = root.querySelector('[data-qf-logo-url]');
    var logoNameInput = root.querySelector('[data-qf-logo-name]');
    var uploadState = { inFlight: false, promise: null };

    function setStatus(kind, nodes) {
      if (!statusEl) return;
      statusEl.className = 'ark-qf__upload-status';
      if (kind) statusEl.classList.add('is-' + kind);
      statusEl.textContent = '';
      (nodes || []).forEach(function (n) { statusEl.appendChild(n); });
      statusEl.hidden = !nodes || !nodes.length;
    }
    function textNode(t) { return document.createTextNode(t); }
    function strongNode(t) { var b = document.createElement('strong'); b.textContent = t; return b; }
    function spinnerNode() {
      var s = document.createElement('span');
      s.className = 'ark-qf__upload-spinner';
      s.setAttribute('aria-hidden', 'true');
      return s;
    }
    function clearLogoFields() {
      if (logoUrlInput) logoUrlInput.value = '';
      if (logoNameInput) logoNameInput.value = '';
    }
    function extOf(name) {
      var m = /\.([A-Za-z0-9]+)$/.exec(name || '');
      return m ? m[1].toLowerCase() : '';
    }

    function uploadLogo(file) {
      uploadState.inFlight = true;
      setStatus('uploading', [spinnerNode(), textNode('Uploading '), strongNode(file.name || 'logo'), textNode('...')]);
      var fd = new FormData();
      fd.append('file', file);
      fd.append('upload_preset', cfg.uploadPreset);
      fd.append('tags', 'arklavo-quote-logo');
      fd.append('context', 'source=request-quote|filename=' + (file.name || ''));

      uploadState.promise = fetch('https://api.cloudinary.com/v1_1/' + cfg.uploadCloud + '/auto/upload', {
        method: 'POST',
        body: fd
      }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error((data && data.error && data.error.message) || 'Upload failed (' + r.status + ')');
          return data;
        });
      }).then(function (data) {
        uploadState.inFlight = false;
        if (!data.secure_url) throw new Error('No URL returned');
        if (logoUrlInput) logoUrlInput.value = data.secure_url;
        if (logoNameInput) logoNameInput.value = file.name || '';
        var link = document.createElement('a');
        link.href = data.secure_url;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = file.name || 'logo';
        setStatus('success', [strongNode('Uploaded: '), link, textNode(' (attached to your quote request)')]);
        return data;
      }).catch(function (err) {
        uploadState.inFlight = false;
        clearLogoFields();
        var retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'ark-qf__retry';
        retry.textContent = 'Try again';
        retry.addEventListener('click', function () { if (fileInput) fileInput.click(); });
        setStatus('error', [strongNode('Could not upload logo: '), textNode((err && err.message) || 'Unknown error'), textNode(' '), retry]);
        throw err;
      });
      return uploadState.promise;
    }

    function handleFile(file) {
      clearLogoFields();
      if (!file) { setStatus('', []); return; }
      if (file.size > cfg.maxBytes) {
        if (fileInput) fileInput.value = '';
        var mb = (file.size / 1024 / 1024).toFixed(1);
        var maxMb = Math.round(cfg.maxBytes / 1024 / 1024);
        setStatus('error', [strongNode('File too large. '), textNode('Your file is ' + mb + ' MB and the maximum is ' + maxMb + ' MB. Compress it, or skip this step and we will collect your logo after sending your quote.')]);
        return;
      }
      var ext = extOf(file.name);
      if (cfg.allowedExts.indexOf(ext) === -1) {
        if (fileInput) fileInput.value = '';
        setStatus('error', [strongNode('Unsupported format: '), textNode('.' + ext + ' - please pick a JPG, PNG, PDF, AI, EPS or SVG file.')]);
        return;
      }
      uploadLogo(file).catch(function () { /* error already shown */ });
    }

    if (fileInput) {
      fileInput.addEventListener('change', function () {
        handleFile(fileInput.files && fileInput.files[0]);
      });
    }
    if (dropzone) {
      ['dragenter', 'dragover'].forEach(function (ev) {
        dropzone.addEventListener(ev, function (e) { e.preventDefault(); dropzone.classList.add('is-drag'); });
      });
      ['dragleave', 'drop'].forEach(function (ev) {
        dropzone.addEventListener(ev, function (e) { e.preventDefault(); dropzone.classList.remove('is-drag'); });
      });
      dropzone.addEventListener('drop', function (e) {
        var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) handleFile(file);
      });
    }

    /* ---------- submit: AJAX to the native contact endpoint, no reload, no jump ---------- */
    function showSuccess() {
      panels.forEach(function (p) { p.hidden = true; });
      if (progressRow) progressRow.hidden = true;
      var header = root.querySelectorAll('.ark-qf__eyebrow, .ark-qf__heading, .ark-qf__sub');
      header.forEach(function (el) { el.hidden = true; });
      if (successPanel) successPanel.hidden = false;
    }
    function fireLead() {
      try {
        var budgetEl = root.querySelector('[data-qf-budget]:checked');
        var cur = tierFor(currentQty());
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'generate_lead',
          form: 'request_quote',
          qty: currentQty(),
          tier: (cur ? cur.pct : 0) + '%',
          budget: budgetEl ? budgetEl.value : ''
        });
      } catch (e) { /* analytics must never block the submit */ }
    }
    /* Mirror the submission to the fulfillment Worker so the requester gets the
       instant quote-received acknowledgement email. Never blocks the native submit. */
    function mirrorQuote(fd) {
      try {
        var get = function (k) { return (fd.get('contact[' + k + ']') || '').toString(); };
        var email = get('email');
        if (!email) return;
        var parts = [];
        ['Product type', 'Quantity', 'Decoration', 'Timing', 'Budget range', 'Company', 'Notes'].forEach(function (k) {
          var v = get(k);
          if (v) parts.push(k + ': ' + v);
        });
        fetch('https://arklavo-fulfillment-router.arklavo.workers.dev/forms/quote', {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email,
            first_name: get('Name'),
            company: get('Company'),
            needed_by: get('Timing'),
            details: parts.join('\n')
          })
        }).catch(function () {});
      } catch (_) {}
    }
    function doSubmit() {
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = cfg.sendingLabel; }
      fireLead();
      var fd = new FormData(form);
      mirrorQuote(fd);
      var done = function () { showSuccess(); };
      fetch(form.getAttribute('action') || '/contact', {
        method: 'POST',
        body: fd,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(done).catch(done);
    }

    function submitIntent() {
      if (step < totalSteps) { showStep(step + 1, true); return; }
      if (!form.checkValidity()) { form.reportValidity(); return; }
      if (!window.fetch) { form.submit(); return; } /* ancient browser: native post */
      if (uploadState.inFlight && uploadState.promise) {
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = cfg.sendingLabel; }
        setStatus('uploading', [spinnerNode(), textNode('Finishing logo upload before submitting...')]);
        var fallback = setTimeout(function () { doSubmit(); }, 30000);
        uploadState.promise.then(function () {
          clearTimeout(fallback);
          doSubmit();
        }).catch(function () {
          clearTimeout(fallback);
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = submitBtn.dataset.qfLabel || submitBtn.textContent; }
          /* leave the error + retry visible; do not submit without the chosen logo */
        });
        return;
      }
      doSubmit();
    }

    /* Third-party scripts on this store intercept `submit` in capture phase and
     * stop propagation without preventing default, which forces a native post.
     * So with JS active we never fire a submit event at all: the button becomes
     * type=button and submission is driven from click + an Enter router. With
     * JS off the button stays type=submit and the native post still works. */
    if (submitBtn) {
      submitBtn.dataset.qfLabel = submitBtn.textContent.trim();
      submitBtn.setAttribute('type', 'button');
      submitBtn.addEventListener('click', submitIntent);
    }
    form.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var t = e.target;
      if (t && t.tagName === 'INPUT' && t.type !== 'checkbox' && t.type !== 'radio') {
        e.preventDefault();
        submitIntent();
      }
    }, true);
    form.addEventListener('submit', function (e) {
      /* Safety net for programmatic requestSubmit or missed paths */
      e.preventDefault();
      submitIntent();
    });

    /* ---------- boot ---------- */
    renderTier();
    if (!root.querySelector('[data-qf-posted]')) showStep(1);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-ark-qf]').forEach(initQuoteForm);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
